import { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Check, ArrowRight, Search, FileCheck, Activity, FileBarChart,
  ShieldCheck, ClipboardList, FileText, UserCheck,
} from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import Seo from "../components/Seo";
import EnquiryForm from "../components/EnquiryForm";
import { SERVICE_LIST } from "../components/calculators";

const PROCESS = [
  { icon: Search, color: "#10B981", title: "Scope the clinical need", text: "Tell us the service, the patient cohort and your timescales — we define volumes and the delivery model together." },
  { icon: FileText, color: "#06B6D4", title: "Receive a formal proposal", text: "A written proposal with the delivery plan, clinical governance arrangements and transparent, itemised pricing." },
  { icon: Activity, color: "#F59E0B", title: "We deliver the service", text: "Experienced clinical pharmacists and pharmacy technicians deliver under named clinical oversight." },
  { icon: FileBarChart, color: "#EF4444", title: "You get the evidence", text: "Structured reporting on activity and outcomes — ready for your board, federation or ICB." },
];

const INCLUDED = [
  { icon: ShieldCheck, color: "#10B981", title: "Clinical governance as standard", text: "Every commission runs under defined clinical governance with named oversight and escalation routes." },
  { icon: UserCheck, color: "#06B6D4", title: "Competency-assured clinicians", text: "Pharmacists and technicians working within agreed clinical competencies, verified before delivery begins." },
  { icon: ClipboardList, color: "#F59E0B", title: "Defined outputs", text: "Agreed volumes, milestones and documentation — you know exactly what will be delivered and when." },
  { icon: FileBarChart, color: "#EF4444", title: "Reporting and accountability", text: "Activity and outcome reporting built into every project, not bolted on afterwards." },
];

const CommissionServices = () => {
  const [selected, setSelected] = useState([]);
  const formRef = useRef(null);

  const toggleService = (key) =>
    setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const pickAndGo = (key) => {
    setSelected((s) => (s.includes(key) ? s : [...s, key]));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const requirementText = useMemo(() => {
    const labels = selected.map((k) => SERVICE_LIST.find((s) => s.key === k)?.label).filter(Boolean);
    return labels.join("; ");
  }, [selected]);

  return (
    <main data-testid="commission-page">
      <Seo
        title="Commission Clinical Services | National Clinical Services"
        description="Commission SMRs, medicines optimisation, prescribing audits, opioid reduction and more — delivered under clinical governance for PCNs and NHS bodies."
      />
      {/* HERO */}
      <section className="relative overflow-hidden pt-32 sm:pt-40 pb-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Commission clinical services</p>
          <h1
            data-testid="commission-heading"
            className="mt-4 font-display font-extrabold tracking-tight leading-[1.04] text-[#0B192C] text-4xl sm:text-5xl lg:text-6xl max-w-4xl"
          >
            <MaskedLine delay={0.15}>Commission Clinical Services</MaskedLine>
            <MaskedLine delay={0.3}><span className="font-serif-accent">Delivered by NCS</span></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-6 text-base sm:text-lg leading-relaxed text-[#475569] max-w-3xl"
          >
            Commission National Clinical Services to deliver defined clinical services and projects
            for your organisation — from Structured Medication Reviews and medicines optimisation
            programmes to prescribing audits and opioid reduction work. Select one service below,
            or combine several within the same enquiry.
          </motion.p>
        </div>
      </section>

      {/* SERVICE CATALOGUE */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl">
              Our Clinical Services Catalogue: Select One Service or Combine Several in a Single Enquiry
            </h2>
            <p className="mt-3 text-base text-[#475569] max-w-2xl">
              Every service is delivered pharmacist-led, under clinical governance, with defined
              outputs and reporting.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICE_LIST.map((s, i) => {
              const isSelected = selected.includes(s.key);
              return (
                <Reveal key={s.key} delay={(i % 3) * 0.07}>
                  <div
                    data-testid={`service-card-${s.key}`}
                    onMouseMove={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                      e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                    }}
                    className={`spotlight-card relative h-full bg-white rounded-3xl p-7 border-2 transition-all duration-300 ${
                      isSelected
                        ? "border-[#10B981] shadow-[0_10px_30px_rgba(11,25,44,0.04)]"
                        : "border-[#E2E8F0] hover:border-[#CBD5E1]"
                    }`}
                  >
                    <button
                      data-testid={`service-select-${s.key}`}
                      onClick={() => toggleService(s.key)}
                      aria-pressed={isSelected}
                      aria-label={`Select ${s.label}`}
                      className={`absolute top-5 right-5 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-colors duration-300 ${
                        isSelected ? "bg-[#10B981] border-[#10B981] text-white" : "border-[#CBD5E1] text-transparent hover:border-[#10B981]"
                      }`}
                    >
                      <Check size={14} strokeWidth={3.5} />
                    </button>
                    <h3 className="font-display text-lg font-bold text-[#0B192C] leading-snug pr-8">{s.label}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-[#475569]">{s.desc}</p>
                    <button
                      data-testid={`service-request-${s.key}`}
                      onClick={() => pickAndGo(s.key)}
                      className="group mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#06B6D4] hover:text-[#0B192C] transition-colors"
                    >
                      Request This Service
                      <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16 sm:py-20 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">How it works</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl">
              How Our Managed Clinical Service Delivery Works
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div data-testid={`commission-process-${i + 1}`} className="relative bg-[#FAFAFC] border border-[#E2E8F0] rounded-3xl p-7 h-full">
                  <span className="absolute top-5 right-6 font-display text-5xl font-extrabold text-[#E2E8F0]">{i + 1}</span>
                  <span
                    className="w-11 h-11 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${s.color}1A`, color: s.color }}
                  >
                    <s.icon size={20} />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-[#0B192C]">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#475569]">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#F59E0B]">Included in every commission</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl">
              What to Expect When You Commission NCS to Deliver a Clinical Service
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {INCLUDED.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.07}>
                <div className="h-full bg-white border border-[#E2E8F0] rounded-3xl p-7 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
                  <span
                    className="w-11 h-11 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${c.color}1A`, color: c.color }}
                  >
                    <c.icon size={20} />
                  </span>
                  <h3 className="mt-5 font-display text-base font-bold text-[#0B192C] leading-snug">{c.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#475569]">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ENQUIRY */}
      <section ref={formRef} className="py-20 sm:py-28 lg:py-32 bg-white border-t border-[#E2E8F0] scroll-mt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">
              {selected.length > 0 ? `${selected.length} service${selected.length > 1 ? "s" : ""} selected` : "Get started"}
            </p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C]">
              Request NCS to Deliver Your Clinical Service
            </h2>
            <p className="mt-4 text-base text-[#475569] max-w-xl mx-auto">
              All commissions are subject to a formal quotation, clinical governance, professional
              competency requirements, availability and contract.
            </p>
          </Reveal>
          <Reveal>
            <EnquiryForm
              journey="commission"
              defaultRequirement={requirementText}
              heading="Tell us about your clinical service requirement"
            />
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default CommissionServices;
